import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Section, Box, Flex, TextField, Text, Button, Table, Badge } from '@radix-ui/themes';

import PageTitle from 'components/layout/PageTitle';
import { instance } from 'api/axios.instance';
import { isFormUsed, toDateInput } from 'util/formVote';

// HWPX 양식 관리 > 상세
export default function FormTemplateDetail() {
  const navigate = useNavigate();
  const { formId } = useParams();

  const [template, setTemplate] = useState(null);
  const [fieldDefs, setFieldDefs] = useState([]);

  const fetchDetail = useCallback(async () => {
    const res = await instance(`/admin/form-template/${formId}`, { method: 'GET' });
    if (res.data.status === 200) {
      const d = res.data.data;
      setTemplate(d);
      try {
        setFieldDefs(JSON.parse(d.fieldDef || '[]'));
      } catch {
        setFieldDefs([]);
      }
    }
  }, [formId]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const handleDownload = async () => {
    try {
      const res = await instance(`/admin/form-template/${formId}/download`, {
        method: 'GET',
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${template.formName}.hwpx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('다운로드 중 오류가 발생했습니다.');
    }
  };

  if (!template) return null;

  return (
    <Section className="template-form-wrap">
      <PageTitle title="양식 상세" />

      <Box className="box box-white">
        <Table.Root className="table-write">
          <Table.Body>
            <Table.Row align="center">
              <Table.ColumnHeaderCell colSpan={2}>
                <Text>양식 코드</Text>
              </Table.ColumnHeaderCell>
              <Table.Cell colSpan={10}>
                <TextField.Root readOnly value={template.formCode} />
              </Table.Cell>
            </Table.Row>
            <Table.Row align="center">
              <Table.ColumnHeaderCell colSpan={2}>
                <Text>양식명</Text>
              </Table.ColumnHeaderCell>
              <Table.Cell colSpan={10}>
                <TextField.Root readOnly value={template.formName} />
              </Table.Cell>
            </Table.Row>
            <Table.Row align="center">
              <Table.ColumnHeaderCell colSpan={2}>
                <Text>사용여부</Text>
              </Table.ColumnHeaderCell>
              <Table.Cell colSpan={10}>
                <Text>{isFormUsed(template) ? '사용' : '사용안함'}</Text>
              </Table.Cell>
            </Table.Row>
            <Table.Row align="center">
              <Table.ColumnHeaderCell colSpan={2}>
                <Text>사용 기간</Text>
              </Table.ColumnHeaderCell>
              <Table.Cell colSpan={10}>
                <Text>
                  {template.startDate && template.endDate ? `${toDateInput(template.startDate)} ~ ${toDateInput(template.endDate)}` : '-'}
                </Text>
              </Table.Cell>
            </Table.Row>
            <Table.Row align="center">
              <Table.ColumnHeaderCell colSpan={2}>
                <Text>카테고리</Text>
              </Table.ColumnHeaderCell>
              <Table.Cell colSpan={10}>
                <TextField.Root readOnly value={template.category || '-'} />
              </Table.Cell>
            </Table.Row>
            <Table.Row align="center">
              <Table.ColumnHeaderCell colSpan={2}>
                <Text>버전</Text>
              </Table.ColumnHeaderCell>
              <Table.Cell colSpan={10}>
                <Text>v{template.currentVer}</Text>
              </Table.Cell>
            </Table.Row>
            <Table.Row align="center">
              <Table.ColumnHeaderCell colSpan={2}>
                <Text>설명</Text>
              </Table.ColumnHeaderCell>
              <Table.Cell colSpan={10}>
                <Text>{template.description || '-'}</Text>
              </Table.Cell>
            </Table.Row>
            <Table.Row align="center">
              <Table.ColumnHeaderCell colSpan={2}>
                <Text>등록일</Text>
              </Table.ColumnHeaderCell>
              <Table.Cell colSpan={10}>
                <Text>{template.regDt}</Text>
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table.Root>
      </Box>

      {fieldDefs.length > 0 && (
        <Box className="box box-white">
          <Text as="h3" size="3" weight="bold" mb="4">
            필드 정의 ({fieldDefs.length}개)
          </Text>
          <Table.Root variant="surface">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>마커</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>레이블</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>타입</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>필수</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {fieldDefs.map(f => (
                <Table.Row key={f.marker}>
                  <Table.Cell>
                    <Text style={{ fontFamily: 'monospace' }} size="2">
                      {f.marker}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>{f.label}</Table.Cell>
                  <Table.Cell>
                    <Badge>{f.type}</Badge>
                  </Table.Cell>
                  <Table.Cell>{f.required ? '✓' : '-'}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Box>
      )}

      <Box>
        <Flex gap="2" justify="end">
          <Button size="2" variant="outline" onClick={handleDownload}>
            양식 다운로드
          </Button>
          <Button size="2" onClick={() => navigate(`/formTemplate/write/${formId}`)}>
            수정
          </Button>
          <Button size="2" variant="outline" onClick={() => navigate('/formTemplate')}>
            목록
          </Button>
        </Flex>
      </Box>
    </Section>
  );
}
